import React, { useState, useEffect } from 'react';
import { budgetService, BudgetTemplateSuggestion, BudgetTemplateCategorySuggestion } from '../../apiServices/budgetService';
import { Modal } from '../UI';
import { useNotification } from '../../contexts/NotificationContext';
import { formatCurrency, toLocalDateString, toUTCDateString } from '../../utils';
import './BudgetTemplateModal.css';

interface BudgetTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBudgetCreated?: () => void;
}

interface CategoryRow extends BudgetTemplateCategorySuggestion {
  selected: boolean;
  editedAmount: number | undefined;
  inputValue: string;
}

type ModalStep = 'setup' | 'loading' | 'template';

function getNextMonday(): string {
  const today = new Date();
  const dow = today.getDay();
  const diff = dow === 1 ? 7 : dow === 0 ? 1 : 8 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  return toLocalDateString(monday);
}

const BudgetTemplateModal: React.FC<BudgetTemplateModalProps> = ({ isOpen, onClose, onBudgetCreated }) => {
  const { showNotification } = useNotification();

  const [step, setStep] = useState<ModalStep>('setup');
  const [startDate, setStartDate] = useState('');
  const [suggestion, setSuggestion] = useState<BudgetTemplateSuggestion | null>(null);
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [budgetName, setBudgetName] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('setup');
      setStartDate(getNextMonday());
      setSuggestion(null);
      setRows([]);
      setBudgetName('');
      setError('');
    }
  }, [isOpen]);

  const handleFetchTemplate = async () => {
    if (!startDate) return;
    setStep('loading');
    setError('');
    try {
      // Send ISO datetime with local offset so backend knows the exact date
      const isoDate = `${startDate}T00:00:00`;
      const data = await budgetService.getWeeklyTemplateSuggestion(isoDate);
      setSuggestion(data);
      setBudgetName(data.suggestedName);
      setRows(
        data.categories.map(c => ({
          ...c,
          selected: c.probability >= 0.5,
          editedAmount: c.suggestedAmount > 0 ? c.suggestedAmount : undefined,
          inputValue: c.suggestedAmount > 0 ? String(c.suggestedAmount) : '',
        }))
      );
      setStep('template');
    } catch (err: any) {
      setError(err.message || 'Помилка завантаження шаблону');
      setStep('setup');
    }
  };

  const handleToggleRow = (idx: number) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  const handleAmountChange = (idx: number, value: string) => {
    const parsed = parseFloat(value);
    setRows(prev => prev.map((r, i) =>
      i === idx
        ? { ...r, inputValue: value, editedAmount: isNaN(parsed) ? undefined : parsed }
        : r
    ));
  };

  const selectedRows = rows.filter(r => r.selected);
  const totalAmount = selectedRows.reduce((sum, r) => sum + (r.editedAmount ?? 0), 0);

  const handleCreateBudget = async () => {
    if (!suggestion || selectedRows.length === 0) return;
    setIsCreating(true);
    setError('');
    try {
      await budgetService.create({
        name: budgetName,
        budgetType: 'Weekly',
        startDate: toUTCDateString(suggestion.startDate),
        endDate: toUTCDateString(suggestion.endDate, '23:59:59'),
        totalPlannedAmount: totalAmount,
        budgetCategories: selectedRows
          .filter(r => (r.editedAmount ?? 0) > 0)
          .map(r => ({ categoryId: r.categoryId, plannedAmount: r.editedAmount! })),
      });
      showNotification('Бюджет успішно створено з шаблону', 'success');
      onBudgetCreated?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Помилка створення бюджету');
    } finally {
      setIsCreating(false);
    }
  };

  const renderFooter = () => {
    if (step === 'setup') {
      return (
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Скасувати
          </button>
          <button
            type="button"
            className="btn btn-primary btm-tmpl-btn"
            onClick={handleFetchTemplate}
            disabled={!startDate}
          >
            <TemplateIcon />
            Отримати шаблон
          </button>
        </>
      );
    }

    if (step === 'loading') {
      return (
        <button type="button" className="btn btn-secondary" disabled>
          Зачекайте...
        </button>
      );
    }

    if (step === 'template') {
      return (
        <>
          <button type="button" className="btn btn-secondary" onClick={() => setStep('setup')} disabled={isCreating}>
            Назад
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreateBudget}
            disabled={isCreating || selectedRows.length === 0}
          >
            {isCreating ? (
              <><span className="btn-spinner-inline"></span>Створення...</>
            ) : 'Створити бюджет'}
          </button>
        </>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (step === 'loading') {
      return (
        <div className="budget-ai-loading">
          <div className="spinner budget-ai-spinner"></div>
          <p className="budget-ai-loading-title">Аналізуємо ваші бюджети...</p>
          <p className="budget-ai-loading-sub">Підбираємо найкращі категорії за вашою статистикою</p>
        </div>
      );
    }

    if (step === 'template' && suggestion) {
      const highProb = rows.filter(r => r.probability >= 0.7);
      const midProb  = rows.filter(r => r.probability >= 0.4 && r.probability < 0.7);
      const lowProb  = rows.filter(r => r.probability < 0.4);

      const renderRow = (r: CategoryRow, idx: number) => {
        const globalIdx = rows.indexOf(r);
        const prob = Math.round(r.probability * 100);
        return (
          <label
            key={r.categoryId}
            className={`btm-tmpl-row ${r.selected ? 'checked' : ''}`}
          >
            <input
              type="checkbox"
              className="budget-ai-checkbox"
              checked={r.selected}
              onChange={() => handleToggleRow(globalIdx)}
            />
            <div className="btm-tmpl-row-body">
              <span className="btm-tmpl-row-name">{r.categoryName}</span>
              <span className="btm-tmpl-row-meta">
                {prob}% ймовірність · {r.occurrenceCount} разів раніше
              </span>
            </div>
            <div className="btm-tmpl-row-amount-wrap">
              <input
                type="number"
                min="0"
                step="1"
                className={`btm-tmpl-amount-input ${!r.selected ? 'disabled' : ''}`}
                value={r.inputValue}
                disabled={!r.selected}
                onChange={e => handleAmountChange(globalIdx, e.target.value)}
                onClick={e => e.stopPropagation()}
                placeholder="0"
              />
              <span className="btm-tmpl-currency">₴</span>
            </div>
          </label>
        );
      };

      return (
        <div className="btm-tmpl-wrap">
          {/* Context banner */}
          {suggestion.historicalBudgetsCount > 0 ? (
            <div className={`budget-ai-context-banner btm-tmpl-confidence-${suggestion.confidenceLevel}`}>
              <InfoIcon />
              <p>
                На основі <strong>{suggestion.historicalBudgetsCount}</strong> попередніх тижневих
                бюджетів для тижня №{suggestion.weekOfMonth} місяця.{' '}
                {suggestion.confidenceLevel === 'high' && 'Висока точність — достатньо даних.'}
                {suggestion.confidenceLevel === 'medium' && 'Середня точність — дані частково враховують інші тижні.'}
                {suggestion.confidenceLevel === 'low' && 'Низька точність — мало точних збігів, враховано всі тижні зі зниженою вагою.'}
              </p>
            </div>
          ) : (
            <div className="btm-tmpl-no-history">
              <InfoIcon />
              <p>Попередніх бюджетів для цього тижня місяця не знайдено — нижче наведено всі ваші категорії. Оберіть потрібні та вкажіть суми.</p>
            </div>
          )}

          {/* Budget name */}
          <div className="form-group">
            <label className="form-label">Назва бюджету</label>
            <input
              type="text"
              className="form-input"
              value={budgetName}
              onChange={e => setBudgetName(e.target.value)}
            />
          </div>

          {/* Dates */}
          <div className="btm-tmpl-dates">
            <span>{suggestion.startDate}</span>
            <span className="btm-tmpl-dates-sep">→</span>
            <span>{suggestion.endDate}</span>
          </div>

          {/* Toolbar */}
          <div className="budget-ai-options-toolbar">
            <span className="budget-ai-options-count">
              Обрано: {selectedRows.length} / {rows.length} · Сума: <strong>{formatCurrency(totalAmount)}&nbsp;₴</strong>
            </span>
            <div className="budget-ai-options-links">
              <button type="button" className="budget-ai-link-btn" onClick={() => setRows(r => r.map(x => ({ ...x, selected: true })))}>Всі</button>
              <span className="budget-ai-link-sep">·</span>
              <button type="button" className="budget-ai-link-btn" onClick={() => setRows(r => r.map(x => ({ ...x, selected: false })))}>Жодної</button>
            </div>
          </div>

          {/* Category rows grouped by probability */}
          <div className="budget-ai-options-list">
            {highProb.length > 0 && (
              <>
                <div className="budget-ai-section-label">
                  <span className="budget-ai-section-dot recurring"></span>
                  Висока ймовірність (&ge;70%)
                </div>
                {highProb.map(renderRow)}
              </>
            )}
            {midProb.length > 0 && (
              <>
                <div className="budget-ai-section-label budget-ai-section-optional">
                  <span className="budget-ai-section-dot" style={{ background: '#f59e0b' }}></span>
                  Середня ймовірність (40–69%)
                </div>
                {midProb.map(renderRow)}
              </>
            )}
            {lowProb.length > 0 && (
              <>
                <div className="budget-ai-section-label budget-ai-section-optional">
                  <span className="budget-ai-section-dot optional"></span>
                  Низька ймовірність (&lt;40%)
                </div>
                {lowProb.map(renderRow)}
              </>
            )}
          </div>

          {error && <div className="error-message general-error">{error}</div>}
        </div>
      );
    }

    // Default: setup
    return (
      <div className="budget-ai-setup-wrap">
        <div className="budget-ai-setup-intro">
          <div className="budget-ai-intro-icon"><TemplateIcon size={28} /></div>
          <p className="budget-ai-intro-text">
            На основі ваших попередніх тижневих бюджетів система підбере категорії
            та суми з найвищою ймовірністю повторення для обраного тижня.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="tmpl-start-date">Початок тижня (понеділок)</label>
          <div className="date-input-wrapper">
            <input
              type="date"
              id="tmpl-start-date"
              className="form-input date-input-custom"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <label htmlFor="tmpl-start-date" className="date-input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </label>
          </div>
          {startDate && (
            <div className="budget-ai-end-date-hint">
              Кінець тижня: {getWeekEnd(startDate)}
            </div>
          )}
        </div>

        {error && <div className="error-message general-error" style={{ marginTop: '12px' }}>{error}</div>}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Шаблон тижневого бюджету"
      size="md"
      closeOnOverlayClick={step !== 'loading'}
      footer={renderFooter()}
    >
      {renderContent()}
    </Modal>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getWeekEnd(startDateStr: string): string {
  const d = new Date(startDateStr + 'T00:00:00');
  d.setDate(d.getDate() + 6);
  return toLocalDateString(d);
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const TemplateIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

export default BudgetTemplateModal;
